import AbstractSegment from "./AbstractSegment";
import ControlPoint from "../ControlPoint";

export default class CurveSegment extends AbstractSegment {
    constructor(provider, start, end, parent, index, cpX, cpY) {
        super(provider, start, end, parent, index);
        this.cp = new ControlPoint(provider, cpX, cpY, this);
    }

    get type() {
        return 'curve';
    }

    display(g) {
        if (this.index === 0) {
            g.vertex(this.start.currentX, this.start.currentY);
        }
        g.quadraticVertex(
            this.cp.currentX, this.cp.currentY,
            this.end.currentX, this.end.currentY
        );
    }

    displayHighlight(ctx) {
        if (this.index === 0) {
            ctx.moveTo(this.start.currentX, this.start.currentY);
        }
        ctx.quadraticCurveTo(
            this.cp.currentX, this.cp.currentY,
            this.end.currentX, this.end.currentY
        );
    }

    displayHover(g) {
        let points = this.sampleCurrentPoints(20);
        for (let i = 0; i < points.length - 1; i++) {
            g.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
        }
    }

    hovered(viewX, viewY, tolerance) {
        let points = this.sampleCurrentPoints(20);
        for (let i = 0; i < points.length - 1; i++) {
            if (this.pointToSegmentDist(viewX, viewY, points[i].x, points[i].y, points[i + 1].x, points[i + 1].y) <= tolerance) {
                return true;
            }
        }
        return false;
    }

    length() {
        let points = this.samplePoints(20);
        let total = 0;
        let prev = { x: this.start.absX, y: this.start.absY };
        for (let pt of points) {
            let dx = pt.x - prev.x;
            let dy = pt.y - prev.y;
            total += Math.sqrt(dx * dx + dy * dy);
            prev = pt;
        }
        return this.start.calculatePixelToDisplayDistance(total);
    }

    midpoint() {
        let t = 0.5;
        let mt = 1 - t;
        return {
            absX: mt * mt * this.start.absX + 2 * mt * t * this.cp.absX + t * t * this.end.absX,
            absY: mt * mt * this.start.absY + 2 * mt * t * this.cp.absY + t * t * this.end.absY,
        };
    }

    export(dx, dy) {
        return [
            this.end.absX + dx,
            this.end.absY + dy,
            "curve",
            this.cp.absX + dx,
            this.cp.absY + dy,
        ];
    }

    exportClosing() {
        return ["closing", "curve", this.cp.absX, this.cp.absY];
    }

    getControlPoints() {
        return [this.cp];
    }

    samplePoints(n) {
        let points = [];
        for (let i = 1; i <= n; i++) {
            let t = i / n;
            let mt = 1 - t;
            points.push({
                x: mt * mt * this.start.absX + 2 * mt * t * this.cp.absX + t * t * this.end.absX,
                y: mt * mt * this.start.absY + 2 * mt * t * this.cp.absY + t * t * this.end.absY,
            });
        }
        return points;
    }

    sampleCurrentPoints(n) {
        let points = [{ x: this.start.currentX, y: this.start.currentY }];
        for (let i = 1; i <= n; i++) {
            let t = i / n;
            let mt = 1 - t;
            points.push({
                x: mt * mt * this.start.currentX + 2 * mt * t * this.cp.currentX + t * t * this.end.currentX,
                y: mt * mt * this.start.currentY + 2 * mt * t * this.cp.currentY + t * t * this.end.currentY,
            });
        }
        return points;
    }

    pointToSegmentDist(px, py, x1, y1, x2, y2) {
        let dx = x2 - x1, dy = y2 - y1;
        let lenSq = dx * dx + dy * dy;
        if (lenSq === 0) return Math.sqrt((px - x1) * (px - x1) + (py - y1) * (py - y1));
        let t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
        let projX = x1 + t * dx, projY = y1 + t * dy;
        return Math.sqrt((px - projX) * (px - projX) + (py - projY) * (py - projY));
    }

    static defaultControlPoint(start, end) {
        let mx = (start.absX + end.absX) / 2;
        let my = (start.absY + end.absY) / 2;
        let dx = end.absX - start.absX;
        let dy = end.absY - start.absY;
        let len = Math.sqrt(dx * dx + dy * dy);
        let offset = len * 0.3;
        return {
            x: mx + (-dy / len) * offset,
            y: my + (dx / len) * offset,
        };
    }
}
